import { useEffect, useRef, useState } from 'react';
import {
  Modal,
  TextInput,
  NumberInput,
  DatePicker,
  DatePickerInput,
  FormGroup,
  Toggle,
  RadioButtonGroup,
  RadioButton,
  FileUploaderDropContainer,
  FileUploaderItem,
  InlineNotification,
  Link,
} from '@carbon/react';
import { Download } from '@carbon/react/icons';
import moment from 'moment';
import { StatusChecklist } from 'components/workflow-status-popover/status-checklist';
import type { StatusItem } from 'pages/crm-tracts-subtracts/types';
import { SERVER_DATE_FORMAT, US_DATE_FORMAT } from 'utils/dates';
import { downloadFromUrl } from 'utils/download-blob';
import { formatBytes, formatNumber } from 'utils/number';
import { useAriesTemplateUpload } from './use-aries-template-upload';
import type {
  AriesMdbBundle,
  AriesMdbExportConfig,
  AriesMdbExportDefaults,
} from './types';
import style from './aries-mdb-export-modal.module.scss';

const MODAL_SUBTITLE =
  "Convert this package's forecasts to an Aries MDB. Forecasts are converted " +
  'to the reference date below.';

/**
 * The four screens the one modal cycles through. `config` is the form; the
 * other three are the live run views (INS-10151 / v6 mock) driven off the
 * kickoff + `useAriesMdbProgress` state.
 */
type ModalView = 'config' | 'progress' | 'completion' | 'failed';

const noop = (): void => undefined;

// MM/YYYY shape (month 01–12, four-digit year).
const BASE_DATE_PATTERN = /^(0[1-9]|1[0-2])\/\d{4}$/;

// Access database file types the "Load a new Template" drop zone accepts.
const TEMPLATE_ACCEPT = ['.mdb', '.accdb'];

// The editable field keys, used to track which fields the user has touched so a
// late-arriving `/new` fetch doesn't clobber in-progress edits (see below).
type FieldKey =
  | 'outputName'
  | 'referenceDate'
  | 'baseDate'
  | 'forecastDurationMonths'
  | 'qualifier'
  | 'sidefileName'
  | 'typecurveLookupName'
  | 'includeCapex'
  | 'gasOpexCorrection';

export interface AriesMdbExportModalProps {
  isOpen: boolean;
  /** True while the kickoff POST is in flight. */
  isLoading: boolean;
  /** True while the `/new` defaults fetch is in flight. */
  isLoadingDefaults: boolean;
  /** Server-derived (or static fallback) defaults to (re)populate fields from. */
  defaults: AriesMdbExportDefaults;
  /** A generic error to surface inline (defaults load failure or kickoff failure). */
  errorMessage?: string | null;
  /**
   * True once a kickoff has succeeded — flips the modal out of the Config form
   * into the live run views (progress → completion / failed). The parent resets
   * it to false when the modal reopens, so a fresh open always shows Config.
   */
  runStarted: boolean;
  /**
   * The restored run's configured output name (from `latest_for_calculation`),
   * shown in the progress subtitle when resuming a run kicked off in a prior
   * session — no in-session submitted name exists then. Null when there is no
   * restored run.
   */
  restoredOutputName?: string | null;
  /** Live per-step checklist rows from `useAriesMdbProgress`. */
  statusItems: StatusItem[];
  /** Overall run status: 'pending' | 'processing' | 'processed' | 'failed'. */
  overallStatus: string;
  /** Completion download bundle, present once the run is `processed`. */
  bundle: AriesMdbBundle | null;
  onClose: () => void;
  onSubmit: (config: AriesMdbExportConfig) => void;
  /**
   * Progress-view secondary action: hide the modal but leave the run going — the
   * caller keeps tracking it server-side (e.g. to auto-download on completion).
   */
  onRunInBackground: () => void;
}

/**
 * The Aries MDB export modal (INS-10553 config + INS-10151 live run). ONE modal
 * that swaps its header, body, and footer across four screens matching the v6
 * mock:
 *
 *  - `config`     — the five field groups (template / output / run parameters /
 *                   naming / economics). Footer: Cancel / Export.
 *  - `progress`   — the live stages checklist while the run builds. Footer:
 *                   Run in background / Exporting… (disabled).
 *  - `completion` — the finished bundle is ready; the checklist stays visible
 *                   with every step Completed. Footer: Close / Download.
 *  - `failed`     — the checklist stays visible (failed row shows its detail).
 *                   Footer: Close.
 *
 * The view is derived from `runStarted` + `overallStatus`; non-config views are
 * gated on `runStarted`. The parent keeps `runStarted` set while a run is in
 * flight, so reopening mid-run resumes the live progress view; it resets to a
 * fresh Config once nothing is running (or the last run finished/failed). The
 * "Load a new Template" source uploads a new `.mdb` (INS-10555) via
 * `useAriesTemplateUpload` and runs the export against it.
 *
 * @param {AriesMdbExportModalProps} props - Component props.
 * @return {JSX.Element} The Aries MDB export modal.
 */
export const AriesMdbExportModal = ({
  isOpen,
  isLoading,
  isLoadingDefaults,
  defaults,
  errorMessage,
  runStarted,
  restoredOutputName = null,
  statusItems,
  overallStatus,
  bundle,
  onClose,
  onSubmit,
  onRunInBackground,
}: AriesMdbExportModalProps): JSX.Element => {
  const [outputName, setOutputName] = useState('');
  const [referenceDate, setReferenceDate] = useState<string | null>(null);
  const [baseDate, setBaseDate] = useState('');
  const [forecastDurationMonths, setForecastDurationMonths] = useState<
    number | null
  >(null);
  const [qualifier, setQualifier] = useState('');
  const [sidefileName, setSidefileName] = useState('');
  const [typecurveLookupName, setTypecurveLookupName] = useState('');
  const [includeCapex, setIncludeCapex] = useState(true);
  const [gasOpexCorrection, setGasOpexCorrection] = useState(true);

  // Which template the run builds from: the global default, or one the user
  // uploads here. Reset on open/close.
  const [templateSource, setTemplateSource] = useState<'default' | 'new'>(
    'default'
  );
  // Owns the "Load a new Template" direct-upload → POST /aries_templates
  // sub-flow. Its `templateId` becomes the run's `templateId` when the user
  // exports on the "new" source. An uploaded template is always promoted to the
  // global default (INS-10555 — no longer user-toggleable).
  const templateUpload = useAriesTemplateUpload(true);
  // Stable alias so the open/close reset effect can depend on it without churn.
  const { reset: resetTemplateUpload } = templateUpload;

  // The output name captured at submit time, used in the progress header so it
  // stays stable regardless of later form/default churn. Reset on reopen.
  const [submittedName, setSubmittedName] = useState('');

  // Whether the user has interacted with the reference date. Reference date can
  // legitimately open blank (calcs with no effective date), so its
  // required/empty error is gated on interaction to avoid flagging it before the
  // user has had a chance to fill it. Reset on open/close.
  const [referenceDateTouched, setReferenceDateTouched] = useState(false);

  // Fields the user has edited since the modal opened. Held in a ref (not state)
  // because it must not trigger re-renders and only guards the populate effect
  // below. Reset whenever the modal opens/closes.
  const touchedFields = useRef<Set<FieldKey>>(new Set());

  /**
   * Marks a field as user-edited so a late `/new` fetch won't overwrite it.
   * @param {FieldKey} field - The field the user just changed.
   */
  const markTouched = (field: FieldKey) => {
    touchedFields.current.add(field);
  };

  // Reset the touched set on every open/close transition. Declared BEFORE the
  // populate effect so, on the open transition, it clears the set first (React
  // runs effects in declaration order) — the populate effect then treats every
  // field as untouched and seeds them all.
  //
  // Keep `submittedName` while a run is being shown so reopening mid-run (resume
  // into progress) keeps the real filename in the subtitle; only clear it when
  // opening a fresh Config (`!runStarted`) for a new run.
  useEffect(() => {
    touchedFields.current = new Set();
    setReferenceDateTouched(false);
    setTemplateSource('default');
    // Clear any prior upload — the file atom is module-level, so a stale
    // `complete` from a previous open must not carry into a fresh run.
    resetTemplateUpload();
    if (!runStarted) setSubmittedName('');
  }, [isOpen, runStarted, resetTemplateUpload]);

  // Populate fields when the modal opens and when fresh defaults arrive from
  // `/new` (the fetch may resolve after the modal is already open — fields stay
  // editable during it). Merge-not-clobber: only set fields the user has NOT
  // edited since open, so a slow-but-successful `/new` never discards in-flight
  // edits (an untouched output name still gets the server stem; a name the user
  // typed while waiting is preserved).
  useEffect(() => {
    if (!isOpen) return;
    const untouched = (field: FieldKey) => !touchedFields.current.has(field);
    if (untouched('outputName')) setOutputName(defaults.outputName ?? '');
    if (untouched('referenceDate')) {
      setReferenceDate(defaults.referenceDate ?? null);
    }
    if (untouched('baseDate')) setBaseDate(defaults.baseDate ?? '');
    if (untouched('forecastDurationMonths')) {
      setForecastDurationMonths(defaults.forecastDurationMonths ?? null);
    }
    if (untouched('qualifier')) setQualifier(defaults.qualifier ?? '');
    if (untouched('sidefileName')) setSidefileName(defaults.sidefileName ?? '');
    if (untouched('typecurveLookupName')) {
      setTypecurveLookupName(defaults.typecurveLookupName ?? '');
    }
    if (untouched('includeCapex')) setIncludeCapex(defaults.includeCapex);
    if (untouched('gasOpexCorrection')) {
      setGasOpexCorrection(defaults.gasOpexCorrection);
    }
  }, [isOpen, defaults]);

  const trimmedName = outputName.trim();
  const baseDateInvalid =
    baseDate.trim() !== '' && !BASE_DATE_PATTERN.test(baseDate.trim());

  // Non-blocking too-short warning (INS-10580): shown when the chosen duration
  // is strictly shorter than the longest PDP horizon. Export stays enabled.
  const showForecastWarning =
    defaults.longestPdpMonths !== null &&
    forecastDurationMonths !== null &&
    forecastDurationMonths < defaults.longestPdpMonths;

  // Required fields — Export is gated until every one is set. The reference date
  // and sidefile name are both hard-required by AR_SIDEFILE, so we block here
  // rather than let a blank value fail the run late (after the expensive source
  // generation + AC_PROPERTY steps already ran); add more fields to this map as
  // the pipeline needs them. The output name is validated separately below.
  const trimmedSidefileName = sidefileName.trim();
  const sidefileNameInvalid = trimmedSidefileName === '';
  // Reference date hard-gates Export; surface the required/empty state (once the
  // user has interacted) so keyboard/screen-reader users see why Export is off,
  // matching the sidefile + base-date fields' invalid treatment.
  const referenceDateInvalid = referenceDateTouched && !referenceDate;
  const requiredFields: Record<string, string | null> = {
    referenceDate,
    sidefileName: trimmedSidefileName || null,
  };
  const hasMissingRequiredField = Object.values(requiredFields).some(
    (value) => !value
  );

  // When "Load a new Template" is selected, Export waits until the upload has
  // finished registering (a real `templateId` exists) — otherwise the run has no
  // template to build from. The default source has no such gate.
  const templateNotReady =
    templateSource === 'new' && templateUpload.templateId === null;

  const configPrimaryDisabled =
    isLoading ||
    isLoadingDefaults ||
    trimmedName === '' ||
    hasMissingRequiredField ||
    baseDateInvalid ||
    templateNotReady;

  const handleSubmit = () => {
    // Guard against an Enter-to-submit that bypasses the disabled button (which
    // would POST a blank-name kickoff while defaults load / are missing).
    if (configPrimaryDisabled) return;
    // Capture the submitted name for the progress header before it can drift.
    setSubmittedName(trimmedName);
    onSubmit({
      outputName: trimmedName,
      // Uploaded template id on the "new" source, else null → current default.
      templateId: templateSource === 'new' ? templateUpload.templateId : null,
      referenceDate: referenceDate || null,
      baseDate: baseDate.trim() || null,
      forecastDurationMonths,
      qualifier: qualifier.trim() || null,
      sidefileName: trimmedSidefileName || null,
      typecurveLookupName: typecurveLookupName.trim() || null,
      includeCapex,
      gasOpexCorrection,
    });
  };

  const currentDefaultName = defaults.template.currentDefaultName ?? 'None set';
  // Same-origin Rails blob path served with `disposition: 'attachment'`, so a
  // plain anchor click downloads the `.mdb` in place (no fetch, no new tab).
  // Null when no default template is configured — the link stays inert then.
  const templateDownloadHref = defaults.template.attachmentHref;

  /**
   * Starts the upload for the file the user drops/picks in the "Load a new
   * Template" zone. Single-file: the last file wins.
   * @param {unknown} _event - The drop/change event (unused).
   * @param {{ addedFiles: File[] }} payload - Carbon's added-files payload.
   */
  const handleAddTemplateFile = (
    _event: unknown,
    { addedFiles }: { addedFiles: File[] }
  ) => {
    const [file] = addedFiles;
    if (file) templateUpload.upload(file);
  };

  // ---------------------------------------------------------------------------
  // Which of the four screens is showing. Non-config views are gated on
  // `runStarted`: the parent keeps it set while a run is in flight (reopen
  // resumes progress) and resets it once nothing is running (reopen → Config).
  // ---------------------------------------------------------------------------
  const view: ModalView = !runStarted
    ? 'config'
    : overallStatus === 'processed'
      ? 'completion'
      : overallStatus === 'failed'
        ? 'failed'
        : 'progress';

  const downloadUrl = bundle?.downloadUrl;
  const bundleFilename = bundle?.filename ?? 'your export';
  const bundleSize =
    typeof bundle?.fileSizeBytes === 'number'
      ? formatBytes(bundle.fileSizeBytes)
      : null;

  // The name shown in the progress subtitle: captured at submit for a run
  // kicked off this session, else the restored run's configured output name
  // (resuming after a tile switch / reload, where no submit happened here).
  const progressName = submittedName || restoredOutputName || '';

  // The failure reason(s), surfaced as always-visible text in the failed view —
  // the checklist's per-row `details` sits behind a tooltip, so the reason gets
  // this direct path too. Keyed on the raw status (falling back to the display
  // label) so a relabel can't break the filter.
  const failureDetails = statusItems
    .filter(
      (item) =>
        (item.status ?? item.value).toLowerCase() === 'failed' && item.details
    )
    .map((item) => item.details)
    .join(' ');

  const handleDownload = () => {
    // The signed SAS URL is already in hand (captured on the completion event),
    // so trigger it directly — no fetch, no tracker POST. Same-tab anchor click
    // (not window.open): the zip response saves in place without a new tab.
    if (downloadUrl) {
      downloadFromUrl(downloadUrl);
      // The download is under way — dismiss the modal rather than leaving a
      // finished-state dialog the user still has to close by hand.
      onClose();
    }
  };

  // The modal heading is CONSTANT across every view (matching the v6 mock — the
  // title stays "Export Aries MDB"; only the subtitle below changes per phase).
  // Per-view footer below: Carbon renders the secondary button only when
  // `secondaryButtonText` is set (failed → none), and routes a secondary click
  // to `onSecondarySubmit` when provided, else to `onRequestClose`.
  const modalHeading = 'Export Aries MDB';

  const primaryButtonText =
    view === 'progress'
      ? 'Exporting…'
      : view === 'completion'
        ? 'Download'
        : view === 'failed'
          ? 'Close'
          : isLoading
            ? 'Exporting…'
            : 'Export';

  const primaryButtonDisabled =
    view === 'progress'
      ? true
      : view === 'completion'
        ? !downloadUrl
        : view === 'failed'
          ? false
          : configPrimaryDisabled;

  const secondaryButtonText =
    view === 'progress'
      ? 'Run in background'
      : view === 'completion'
        ? 'Close'
        : view === 'config'
          ? 'Cancel'
          : undefined; // failed → single Close button

  const handlePrimary =
    view === 'completion'
      ? handleDownload
      : view === 'failed'
        ? onClose
        : view === 'config'
          ? handleSubmit
          : noop; // progress → primary is disabled

  // Config: leave `onSecondarySubmit` unset so Cancel falls through to
  // `onRequestClose`, which is gated on loading (no close mid-submit).
  const handleSecondary =
    view === 'progress'
      ? onRunInBackground
      : view === 'completion'
        ? onClose
        : undefined;

  const handleRequestClose =
    view === 'config'
      ? isLoading
        ? undefined
        : onClose
      : view === 'progress'
        ? onRunInBackground
        : onClose; // completion / failed

  // Every post-kickoff view keeps the stages checklist visible (matching the v6
  // mock) — progress shows live status, completion shows all steps Completed,
  // failed shows the failed row. Only the config view omits it.
  const isRunView = view !== 'config';

  return (
    <Modal
      open={isOpen}
      modalHeading={modalHeading}
      // Distinct from any footer button label (the Failed view's footer is
      // "Close") so the header X and the footer button never collide.
      closeButtonLabel="Close dialog"
      primaryButtonText={primaryButtonText}
      primaryButtonDisabled={primaryButtonDisabled}
      secondaryButtonText={secondaryButtonText}
      onRequestSubmit={handlePrimary}
      onSecondarySubmit={handleSecondary}
      onRequestClose={handleRequestClose}
      className={style.ariesMdbExportModal}
    >
      {view === 'config' && (
        <p className={style.description}>{MODAL_SUBTITLE}</p>
      )}

      {view === 'progress' && (
        <p className={style.description}>
          Exporting{' '}
          <strong>
            {progressName ? `${progressName}.mdb` : 'your Aries MDB'}
          </strong>{' '}
          — this runs server-side; you can leave it running and come back.
        </p>
      )}

      {view === 'completion' && (
        <p className={style.description}>
          Export complete — <strong>{bundleFilename}</strong> is ready to
          download{bundleSize ? ` (${bundleSize})` : ''}.
        </p>
      )}

      {view === 'failed' && (
        <p className={style.description}>
          The export didn&apos;t finish. Review the steps below to see what
          failed.
        </p>
      )}

      {view === 'failed' && failureDetails ? (
        <InlineNotification
          className={style.notification}
          kind="error"
          // Title is the reason label (not a second "Export failed", which the
          // modal heading already announces) so screen readers don't repeat it.
          title="Failure reason"
          subtitle={failureDetails}
          lowContrast
          hideCloseButton
        />
      ) : null}

      {isRunView && (
        <div className={style.checklist}>
          <StatusChecklist statusItems={statusItems} showStatusIcon />
        </div>
      )}

      {view === 'config' && errorMessage ? (
        <InlineNotification
          className={style.notification}
          kind="error"
          title="Aries MDB export"
          subtitle={errorMessage}
          lowContrast
          hideCloseButton
        />
      ) : null}

      {view === 'config' && (
        <div className={style.groupGrid}>
          <FormGroup
            className={style.group}
            legendText="MDB template"
            disabled={isLoading}
          >
            <p className={style.groupHelper}>
              The Aries database this export is built from.
            </p>
            <RadioButtonGroup
              name="aries-mdb-template-source"
              legendText=""
              valueSelected={templateSource}
              orientation="vertical"
              onChange={(value: string | number | undefined) =>
                setTemplateSource(value === 'new' ? 'new' : 'default')
              }
            >
              <RadioButton
                id="aries-mdb-template-default"
                value="default"
                labelText="Use the default template"
              />
              <RadioButton
                id="aries-mdb-template-new"
                value="new"
                labelText="Load a new Template"
              />
            </RadioButtonGroup>
            {templateSource === 'default' ? (
              <>
                <p className={style.templateDetail}>
                  <span className={style.templateLabel}>Current default: </span>
                  <span className={style.templateName}>
                    {currentDefaultName}
                  </span>
                </p>
                <Link
                  className={style.templateDownload}
                  href={templateDownloadHref ?? undefined}
                  // Server sends `disposition: 'attachment'`; `download` lets
                  // the browser save with the blob's own filename. Left enabled
                  // even with no default template — it just has no href then.
                  download
                  renderIcon={Download}
                >
                  Download a copy
                </Link>
              </>
            ) : (
              <div className={style.templateUpload}>
                <FileUploaderDropContainer
                  accept={TEMPLATE_ACCEPT}
                  multiple={false}
                  labelText="Drag and drop an .mdb file here, or click to upload"
                  // Don't accept another file while one is uploading/registering.
                  disabled={isLoading || templateUpload.isBusy}
                  onAddFiles={handleAddTemplateFile}
                />
                <p className={style.templateUploadNote}>
                  An uploaded MDB file will become the default for all users.
                </p>
                {templateUpload.fileName ? (
                  <FileUploaderItem
                    name={templateUpload.fileName}
                    // 'uploading' shows the spinner; anything terminal shows
                    // 'edit' so the remove (×) is available.
                    status={templateUpload.isBusy ? 'uploading' : 'edit'}
                    invalid={templateUpload.status === 'error'}
                    errorBody={templateUpload.error ?? undefined}
                    iconDescription="Remove file"
                    onDelete={() =>
                      !templateUpload.isBusy && templateUpload.reset()
                    }
                  />
                ) : null}
                {templateUpload.status === 'complete' ? (
                  <InlineNotification
                    className={style.notification}
                    kind="success"
                    title="Template uploaded"
                    subtitle="This export will use it, and it is now the default for everyone."
                    lowContrast
                    hideCloseButton
                  />
                ) : null}
              </div>
            )}
          </FormGroup>

          <FormGroup
            className={style.group}
            legendText="Output"
            disabled={isLoading}
          >
            <TextInput
              id="aries-mdb-output-name"
              labelText="Name for the Aries .mdb output"
              maxLength={255}
              value={outputName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                markTouched('outputName');
                // Sanitize as the user types so the field only ever holds a
                // filename-safe stem (mirrors the server-side guard): anything
                // outside [A-Za-z0-9._-] — spaces, path separators, etc. —
                // becomes "_", so what they see is the delivered blob name.
                setOutputName(e.target.value.replace(/[^A-Za-z0-9._-]/g, '_'));
              }}
            />
            <p className={style.filenamePreview}>
              Output filename: {trimmedName || '—'}.mdb
            </p>
          </FormGroup>

          <div className={style.divider} aria-hidden="true" />

          <FormGroup
            className={style.group}
            legendText="Run parameters"
            disabled={isLoading}
          >
            <DatePicker
              datePickerType="single"
              value={
                referenceDate
                  ? moment(referenceDate, SERVER_DATE_FORMAT).format(
                      US_DATE_FORMAT
                    )
                  : ''
              }
              onChange={(dates: Date[]) => {
                const [picked] = dates;
                // Carbon empties flatpickr via clear() when the value prop goes
                // blank (the populate effect reseeding from the static
                // fallback), and clear() fires this onChange. That programmatic
                // echo arrives after the state is already null — a real user
                // clear fires while a date is still set. Ignore the echo, or it
                // marks the field touched and blocks the `/new` defaults from
                // seeding it on reopen.
                if (!picked && referenceDate === null) return;
                markTouched('referenceDate');
                setReferenceDateTouched(true);
                setReferenceDate(
                  picked ? moment(picked).format(SERVER_DATE_FORMAT) : null
                );
              }}
            >
              <DatePickerInput
                id="aries-mdb-reference-date"
                labelText="Reference date (required)"
                placeholder={US_DATE_FORMAT}
                invalid={referenceDateInvalid}
                invalidText="Reference date is required."
              />
            </DatePicker>
            <TextInput
              className={style.fieldGap}
              id="aries-mdb-base-date"
              labelText="Base date (MM/YYYY)"
              placeholder="MM/YYYY"
              value={baseDate}
              invalid={baseDateInvalid}
              invalidText="Enter a date as MM/YYYY."
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                markTouched('baseDate');
                setBaseDate(e.target.value);
              }}
            />
            <NumberInput
              className={style.fieldGap}
              id="aries-mdb-forecast-duration"
              label="Forecast duration (months)"
              min={1}
              step={1}
              allowEmpty
              value={forecastDurationMonths ?? ''}
              onChange={(
                _event,
                { value }: { value: number | string; direction: string }
              ) => {
                const parsed = Number(value);
                markTouched('forecastDurationMonths');
                setForecastDurationMonths(
                  value === '' || Number.isNaN(parsed) ? null : parsed
                );
              }}
            />
            {showForecastWarning ? (
              <InlineNotification
                className={style.notification}
                kind="warning"
                title="Forecast duration is shorter than the longest PDP forecast"
                subtitle={`The longest PDP forecast runs ${formatNumber({
                  value: defaults.longestPdpMonths,
                  minPrecision: 0,
                })} months; this export will truncate forecasts to ${formatNumber(
                  {
                    value: forecastDurationMonths,
                    minPrecision: 0,
                  }
                )} months.`}
                lowContrast
                hideCloseButton
              />
            ) : null}
          </FormGroup>

          <FormGroup
            className={style.group}
            legendText="Naming"
            disabled={isLoading}
          >
            <TextInput
              id="aries-mdb-qualifier"
              labelText="Qualifier"
              placeholder="e.g. 2026Q1_BASE"
              value={qualifier}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                markTouched('qualifier');
                setQualifier(e.target.value);
              }}
            />
            <TextInput
              className={style.fieldGap}
              id="aries-mdb-sidefile-name"
              labelText="Sidefile name"
              placeholder="e.g. bluefish_sidefile"
              value={sidefileName}
              invalid={sidefileNameInvalid}
              invalidText="Sidefile name is required."
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                markTouched('sidefileName');
                setSidefileName(e.target.value);
              }}
            />
            <TextInput
              className={style.fieldGap}
              id="aries-mdb-typecurve-lookup-name"
              labelText="Typecurve lookup name"
              placeholder="e.g. bluefish_tc_lookup"
              value={typecurveLookupName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                markTouched('typecurveLookupName');
                setTypecurveLookupName(e.target.value);
              }}
            />
          </FormGroup>

          <FormGroup
            className={style.group}
            legendText="Economics & adjustments"
            disabled={isLoading}
          >
            <div className={style.toggleGroup}>
              <Toggle
                size="sm"
                id="aries-mdb-include-capex"
                labelText="Include capex — adds capex rows to AC_ECONOMIC"
                labelA="Off"
                labelB="On"
                toggled={includeCapex}
                onToggle={(checked: boolean) => {
                  markTouched('includeCapex');
                  setIncludeCapex(checked);
                }}
              />
              <Toggle
                size="sm"
                id="aries-mdb-gas-opex-correction"
                labelText="Gas-opex correction — re-allocate gas opex to corrected basis"
                labelA="Off"
                labelB="On"
                toggled={gasOpexCorrection}
                onToggle={(checked: boolean) => {
                  markTouched('gasOpexCorrection');
                  setGasOpexCorrection(checked);
                }}
              />
            </div>
          </FormGroup>
        </div>
      )}
    </Modal>
  );
};
